<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import type { PvFormPayload } from "@modules/pv/components/dimensionnement/form/pv-form.types"

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        sourceId?: string | null
        segment?: string
        analyseId?: string | null
        form?: PvFormPayload | null
        idBuilding?: string | null
        idExtrapolation?: string | null
        initialStep?: "general" | "roof" | "finish"
    }>(),
    {
        sourceId: null,
        segment: "C5",
        analyseId: null,
        form: null,
        idBuilding: null,
        idExtrapolation: null,
        initialStep: "general",
    }
)

const visible = defineModel<boolean>("visible", {
    required: true,
    default: false,
})

const emit = defineEmits<{
    (event: "close"): void
    (event: "create:analyse", payload: PvFormPayload): void
    (event: "update:analyse", payload: PvFormPayload): void
}>()

// ================================
// DONNÉES CALCULÉES
// ================================
/**
 * Libellé d’en-tête du dialogue : création ou modification d’étude selon `analyseId`.
 */
const getDimensionnementDialogTitle = computed((): string =>
    props.analyseId ? "Modification de l'étude" : "Créer une nouvelle étude"
)

// ================================
// INTERACTIONS
// ================================
/**
 * Ferme le dialogue de dimensionnement PV.
 * @returns void
 */
function onCloseDialog(): void {
    visible.value = false
    emit("close")
}

/**
 * Relaye la creation d'une analyse PV au parent.
 * @param payload - Donnees de l'analyse PV creee.
 * @returns void
 */
function onCreateAnalyse(payload: PvFormPayload): void {
    emit("create:analyse", payload)
}

/**
 * Relaye la mise a jour d'une analyse PV au parent.
 * @param payload - Donnees de l'analyse PV mises a jour.
 * @returns void
 */
function onUpdateAnalyse(payload: PvFormPayload): void {
    emit("update:analyse", payload)
}
</script>

<template>
    <!-- ================================ -->
    <!-- DIALOG PRINCIPAL -->
    <!-- ================================ -->
    <BaseDialog
        v-model:visible="visible"
        modal
        dismissable-mask
        class="pv-dialog-dimensionnement w-[95vw] md:w-[70vw] xl:w-[60vw]"
    >
        <template #header>
            <h2 class="text-lg font-semibold text-primary">
                {{ getDimensionnementDialogTitle }}
            </h2>
        </template>

        <PVForm
            :form="props.form"
            :id-source="props.sourceId"
            :id-building="props.idBuilding"
            :id-extrapolation="props.idExtrapolation || props.analyseId"
            :is-open="visible"
            :initial-step="props.initialStep"
            @[`click:close`]="onCloseDialog"
            @[`create:analyse`]="onCreateAnalyse"
            @[`update:analyse`]="onUpdateAnalyse"
        />
    </BaseDialog>
</template>
